import { useEffect, useState } from "react";
import styles from "./Apps.module.css";

/**
 * Shape of an entry in the production registry.json (see
 * scripts/publish-apps.mjs). The website only needs id, name, and icon —
 * the channel/manifestUrl block exists for the Tauri app loader.
 */
interface RegistryApp {
  id: string;
  name: string;
  icon: { kind: "svg"; source: string } | string;
  /** From the published manifest's `description`. May be absent on
   *  older entries that predate publish-apps relaying it. */
  description?: string;
  /** From the published manifest's `category`. Same caveat. */
  category?: string;
  channels: { stable: { version: string; manifestUrl: string } };
}

interface RegistryResponse {
  schemaVersion: number;
  /**
   * Ordered list of app ids that frame Atlas's pitch. Surfaced first
   * in the marketing grid, in this exact order. Sourced from
   * atlas.config.json → registry.json on R2 — change the order with
   * an apps:publish, no website redeploy needed.
   */
  essentials?: string[];
  apps: RegistryApp[];
}

/**
 * Website-side polish that doesn't belong in the manifest schema:
 * the badge pills and (rarely) a category override. Tagline copy comes
 * straight from the registry's `description` field — the manifests
 * are the single source of truth for that.
 *
 * Apps that ship in the registry but don't have a metadata entry
 * here render with safe defaults — generic "App" category (unless
 * the manifest declares one) and an "Offline" badge.
 */
/** Badge entry: plain string defaults to teal; object form lets a
 *  specific badge opt into the mustard tint. Mixed tones in one row
 *  are fine — render handles both. */
type Badge = string | { text: string; tone?: "teal" | "mustard" };

interface AppMeta {
  /**
   * Optional category override. Leave undefined — the marketing card
   * falls back to the app's own manifest category (relayed via the
   * registry). Specify here ONLY if marketing wants to nudge the
   * label differently from what the app declares.
   */
  category?: string;
  /** Empty array = no pill. Multiple entries render side-by-side. */
  badges: Badge[];
}

const FALLBACK_META: AppMeta = {
  category: "App",
  badges: ["Offline"],
};

function badgeText(b: Badge): string {
  return typeof b === "string" ? b : b.text;
}
function badgeTone(b: Badge): "teal" | "mustard" {
  if (typeof b === "string") return "teal";
  return b.tone ?? "teal";
}

const APP_META: Record<string, AppMeta> = {
  "system.stickies": { badges: ["Offline"] },
  "system.notes": { badges: ["Offline"] },
  "system.music": { badges: ["Offline"] },
  "system.layouts": { badges: ["Offline"] },
  "system.kepler": {
    // Second pill nods at the "wait, an actually-offline local AI?" reaction —
    // mustard to break up the teal line of offline pills.
    badges: ["Offline", { text: "yes, really", tone: "mustard" }],
  },
  "system.movies": { badges: ["Offline"] },
  "system.browser": {
    // No badge — "offline" doesn't fit a browser, "lightweight" felt forced.
    badges: [],
  },
  "system.chat": {
    category: "Communication",
    // "Offline" doesn't apply (chat literally requires a peer + relay);
    // the encryption guarantee is the more honest signal anyway.
    // Mustard tint to stand apart from the offline-pill teal sea.
    badges: [{ text: "E2E Encrypted", tone: "mustard" }],
  },
  "system.mail": {
    category: "Communication",
    // Mail talks directly to the user's IMAP/SMTP — nothing routes
    // through Atlas. Mustard mirrors the chat card to stand apart from
    // the teal "Offline" sea.
    badges: [{ text: "Direct IMAP/SMTP", tone: "mustard" }],
  },
  "system.terminal": { badges: ["Offline"] },
  "system.calculator": { badges: ["Offline"] },
  "system.settings": { badges: ["Offline"] },
};

const REGISTRY_URL = "https://assets.atlasdesk.co/apps/registry.json";
const INITIAL_LIMIT = 8;

/**
 * Map of app ids to per-app landing pages. Only apps with a dedicated
 * marketing page belong in here — the rest still render through the
 * `AppPagePlaceholder`, and clicking those would dead-end on a "coming
 * soon" stub, which is worse than no click target. Add an entry here
 * when you ship a new per-app page (see `pages/StickiesPage.tsx` /
 * `pages/NotesPage.tsx` for the pattern).
 */
const APP_LANDING_PATHS: Record<string, string> = {
  "system.stickies": "/stickies",
  "system.notes": "/notes",
};

/**
 * Apps — a curated shelf, populated from the live app registry on R2.
 * What ships in production shows up here automatically. The only
 * thing the website keeps in its own bundle is per-app marketing
 * polish (tagline / badge) — ordering itself lives in the registry's
 * `essentials` list.
 */
export function Apps() {
  const [apps, setApps] = useState<RegistryApp[] | null>(null);
  const [essentials, setEssentials] = useState<string[]>([]);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(REGISTRY_URL, { cache: "no-cache" })
      .then((res) => (res.ok ? (res.json() as Promise<RegistryResponse>) : null))
      .then((data) => {
        if (cancelled || !data) return;
        setApps(data.apps ?? []);
        setEssentials(data.essentials ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Sort by registry's `essentials` list: essential apps in their
  // declared order, then everything else alphabetically. If the
  // registry is older and lacks `essentials`, fall through to pure
  // alphabetical so the grid still renders sensibly.
  const sorted = (apps ?? []).slice().sort((a, b) => {
    if (essentials.length > 0) {
      const ia = essentials.indexOf(a.id);
      const ib = essentials.indexOf(b.id);
      const isEssentialA = ia >= 0;
      const isEssentialB = ib >= 0;
      if (isEssentialA && isEssentialB) return ia - ib;
      if (isEssentialA) return -1;
      if (isEssentialB) return 1;
    }
    return a.name.localeCompare(b.name);
  });

  const total = sorted.length;
  const visible =
    showAll || total <= INITIAL_LIMIT ? sorted : sorted.slice(0, INITIAL_LIMIT);
  const remaining = total - visible.length;

  return (
    <section
      id="apps"
      className={`${styles.section} atlasDotsBg`}
      aria-labelledby="apps-title"
    >
      <div className={`atlasContainer ${styles.inner}`}>
        <header className={styles.header}>
          <div>
            <span className="atlasEyebrow">The Apps</span>
            <h2 id="apps-title" className={styles.title}>
              Tiny tools, all in one place.
            </h2>
            <p className={styles.lede}>
              A curated shelf — not a marketplace. Each app does one thing
              well and respects your attention.
            </p>
            <p className={styles.ledeAccent}>
              Install only the ones you want. No bloat.
            </p>
          </div>
          {total > 0 ? (
            <span className={styles.tally}>
              <span className={styles.tallyNum}>{total}</span>
              <span className={styles.tallyLabel}>available now</span>
            </span>
          ) : null}
        </header>

        <div className={styles.grid}>
          {visible.map((app) => {
            // Per-card copy is layered: the app's manifest is the
            // source of truth for tagline + category (relayed via the
            // registry). The local APP_META only carries badges and
            // the rare category override — the marketing-voice tagline
            // belongs in the manifest itself, not duplicated here.
            const meta = APP_META[app.id];
            const tagline = app.description || "";
            const category =
              meta?.category || app.category || FALLBACK_META.category;
            const badges = meta?.badges ?? FALLBACK_META.badges;
            const landingPath = APP_LANDING_PATHS[app.id];

            const cardBody = (
              <>
                <AppGridIcon icon={app.icon} />
                <header className={styles.cardHead}>
                  <h3 className={styles.cardName}>{app.name}</h3>
                  <span className={styles.cardTag}>{category}</span>
                </header>
                {tagline ? (
                  <p className={styles.cardTagline}>{tagline}</p>
                ) : null}
                {badges.length > 0 ? (
                  <div className={styles.cardBadges} aria-hidden>
                    {badges.map((b) => {
                      const text = badgeText(b);
                      const tone = badgeTone(b);
                      return (
                        <span
                          key={text}
                          className={
                            tone === "mustard"
                              ? `${styles.badge} ${styles.badgeMustard}`
                              : styles.badge
                          }
                        >
                          {text}
                        </span>
                      );
                    })}
                  </div>
                ) : null}
              </>
            );

            // Apps with a per-app landing page render as anchors so the
            // whole tile is clickable; everything else stays a plain
            // article (no dead-end clicks to placeholder pages).
            return landingPath ? (
              <a
                key={app.id}
                href={landingPath}
                className={styles.card}
                aria-label={`${app.name} — learn more`}
              >
                {cardBody}
              </a>
            ) : (
              <article key={app.id} className={styles.card}>
                {cardBody}
              </article>
            );
          })}
        </div>

        {remaining > 0 ? (
          <div className={styles.moreRow}>
            <button
              type="button"
              className={styles.moreButton}
              onClick={() => setShowAll(true)}
            >
              See {remaining} more <span aria-hidden>↓</span>
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * Renders the registry's icon field. Today every app ships an inline SVG
 * (`{ kind: "svg", source }`); the string branch is a forward-compat
 * escape hatch for icon-pack keys we might re-introduce later — for now
 * it just shows nothing rather than crashing.
 */
function AppGridIcon({ icon }: { icon: RegistryApp["icon"] }) {
  if (typeof icon === "object" && icon.kind === "svg") {
    return (
      <div
        className={styles.cardIcon}
        aria-hidden
        dangerouslySetInnerHTML={{ __html: icon.source }}
      />
    );
  }
  return <div className={styles.cardIcon} aria-hidden />;
}
